import { createSelector, createSlice } from '@reduxjs/toolkit';
import qs from 'qs';
import * as R from 'ramda';

// Utils
import http from 'utils/http';
import { createSnack } from 'snackbar/snackbarSlice';
import { getActivationType } from 'feature/loading-management/utils/helpers';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import {
  getCurrentSelection,
  setSelfAuthorizationDialogOpen,
  updateSelfAuthorizations,
} from './selfAuthorizationsSlice';

const initialState = {
  authorizationBy: 'location',
  enabledOptions: {},
  errorFetchingInactiveSelfAuthorizations: '',
  errorFetchingViewByList: '',
  forceClose: false,
  hasUnsavedChanges: false,
  inactiveListLoading: false,
  inactiveSelfAuthorizations: [],
  params: {
    affiliate: {},
    selfAuthType: undefined,
  },
  submittingBatch: false,
  viewByList: [],
};

const reducers = {
  resetEnabledOptions(state) {
    return R.assoc('enabledOptions', {}, state);
  },
  resetWizardData(state, { payload }) {
    // This is resetting all but authorizationBy
    // Solves the issue in CASREF-1425 mentioned by Jason in PR Comments
    // https://issues.mmplp.net:8443/browse/CASREF-1425
    return R.assoc('authorizationBy', R.defaultTo('location', payload), initialState);
  },
  setAuthorizationBy(state, { payload }) {
    return R.assoc('authorizationBy', payload, state);
  },
  setEnabledOptions(state, { payload: { id, selected } }) {
    return R.mergeRight(state, {
      enabledOptions: { ...state.enabledOptions, [id]: selected },
      hasUnsavedChanges: true,
    });
  },
  setErrorFetchingInactiveSelfAuthorizations(state, { payload }) {
    return R.assoc('errorFetchingInactiveSelfAuthorizations', payload.error, state);
  },
  setErrorFetchingViewByList(state, { payload }) {
    return R.assoc('errorFetchingViewByList', payload.error, state);
  },
  setForceClose(state, { payload }) {
    return R.assoc('forceClose', payload, state);
  },
  setHasUnsavedChanges(state, { payload }) {
    return R.assoc('hasUnsavedChanges', payload, state);
  },
  setInactiveListLoading(state, { payload }) {
    return R.assoc('inactiveListLoading', payload, state);
  },
  setInactiveSelfAuthorizations(state, { payload }) {
    return R.assoc('inactiveSelfAuthorizations', payload.list, state);
  },
  setParams(state, { payload }) {
    return R.assoc('params', { ...state.params, ...payload }, state);
  },
  setSubmittingBatch(state, { payload }) {
    return R.assoc('submittingBatch', payload, state);
  },
  setViewByList(state, { payload }) {
    return R.assoc('viewByList', payload.list, state);
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'createNewSelfAuth',
  reducers,
});

export const {
  resetWizardData,
  setAuthorizationBy,
  setViewByList,
  setParams,
  setInactiveListLoading,
  setInactiveSelfAuthorizations,
  setErrorFetchingInactiveSelfAuthorizations,
  setErrorFetchingViewByList,
  setEnabledOptions,
  setSubmittingBatch,
  setForceClose,
  setHasUnsavedChanges,
  resetEnabledOptions,
} = actions;

// Selectors
const getViewByListSelector = state => state.loadingManagement.createNewSelfAuth.viewByList;
export const getViewByListOptions = createSelector(getViewByListSelector, items =>
  R.map(({ id, nameShort }) => ({ label: nameShort, value: id }), items)
);

// Step 2 Selectors
const getInactiveList = R.path([
  'loadingManagement',
  'createNewSelfAuth',
  'inactiveSelfAuthorizations',
]);
const getEnabledOptions = R.path(['loadingManagement', 'createNewSelfAuth', 'enabledOptions']);
export const getInactiveListOptions = createSelector(
  getInactiveList,
  getEnabledOptions,
  (inactiveList, enabledOptions) =>
    R.map(
      ({ id, nameShort, proprietary }) => ({
        label: nameShort,
        proprietary,
        selected: !!enabledOptions[id],
        value: id,
      }),
      inactiveList
    )
);
export const getAllEnabled = createSelector(getInactiveListOptions, R.all(R.prop('selected')));
export const getIdentifier = createSelector(
  // get locationId or productId from params
  R.compose(
    R.find(x => x),
    R.props(['locationId', 'productId']),
    R.path(['loadingManagement', 'createNewSelfAuth', 'params'])
  ),
  getViewByListSelector,
  (testId, viewByList) => R.o(R.prop('nameShort'), R.find(R.propEq('id', testId)))(viewByList)
);
export const getStepTwoDisableContinue = createSelector(
  getInactiveListOptions,
  R.none(R.prop('selected'))
);

export const openCreateSelfAuthorizationCloseDialog = dialogType => (dispatch, getState) => {
  if (dialogType === '') {
    dispatch(setSelfAuthorizationDialogOpen(''));
    return;
  }

  const { hasUnsavedChanges, submittingBatch } = getState().loadingManagement.createNewSelfAuth;

  if (hasUnsavedChanges && !submittingBatch) {
    dispatch(
      openConfirmationDialog({
        cancelAction: () => {},
        confirmAction: () => dispatch(setForceClose(true)),
        header: 'Unsaved Changes',
        leftButtonActionType: 'confirm',
        leftButtonText: 'Continue',
        message:
          'Are you sure you want to leave this page? You have unsaved Self Authorization changes.',
        params: {},
        rightButtonActionType: 'cancel',
        rightButtonText: 'Keep Editing',
      })
    );
    return;
  }

  dispatch(setSelfAuthorizationDialogOpen('batchActivate'));
};

// Step 3 selector
export const getInactiveSelfAuthorizationsSelected = createSelector(
  getInactiveList,
  state => state.loadingManagement.createNewSelfAuth.enabledOptions,
  (inactiveList, enabledOptions) => {
    return R.compose(
      R.map(x => R.find(R.propEq('id', parseInt(x)))(inactiveList)),
      R.keys,
      R.filter(x => x)
    )(enabledOptions);
  }
);

// Actions
export const fetchInactiveSelfAuthorizations = () => async (dispatch, getState) => {
  const {
    params: {
      affiliate: { customerIdName, dataCompanyId },
      selfAuthType,
      locationId,
      productId,
    },
  } = getState().loadingManagement.createNewSelfAuth;
  const queryParams = {
    affiliate: {
      companyCustomerIdName: customerIdName,
      dataCompanyId,
    },
    locationId,
    productId,
    selfAuthorizationType: selfAuthType,
  };
  const paramString = qs.stringify(queryParams, {
    allowDots: true,
    encode: false,
  });
  const prefix = locationId
    ? '/get-location-inactive-products?'
    : '/get-product-inactive-locations?';
  dispatch(setInactiveListLoading(true));
  const URL = `self-authorizations${prefix}${paramString}`;
  http
    .get(URL)
    .then(({ data }) => dispatch(setInactiveSelfAuthorizations({ list: data })))
    .catch(err =>
      dispatch(
        createSnack({
          message: `Error fetching inactive products/locations: ${err.response.data.title}`,
          options: {
            variant: 'error',
          },
        })
      )
    )
    .finally(() => dispatch(setInactiveListLoading(false)));
};

export const fetchViewByList = (params, authBy) => async dispatch => {
  const {
    affiliate: { dataCompanyId, customerIdName },
    selfAuthorizationType,
  } = params;
  const locationQueryParams = { dataCompanyId, selfAuthorizationType };
  const productQueryParams = {
    affiliate: { companyCustomerIdName: customerIdName, dataCompanyId },
    selfAuthorizationType,
  };
  const queryParams = authBy === 'location' ? locationQueryParams : productQueryParams;
  const paramString = qs.stringify(queryParams, {
    allowDots: true,
    encode: false,
  });

  const URL = `self-authorizations/get-${authBy}s?${paramString}`;
  http
    .get(URL)
    .then(({ data }) => dispatch(setViewByList({ list: data })))
    .catch(err =>
      dispatch(
        createSnack({
          message: `Error fetching ${authBy}s: ${err.response.data.title}`,
          options: {
            variant: 'error',
          },
        })
      )
    );
};

export const batchActivate =
  ({ listType, ...postBody }) =>
  async (dispatch, getState) => {
    const activationType = getActivationType(listType);
    const { locationId, productId } = postBody;
    const { id } = getCurrentSelection(getState());

    const URL = `self-authorizations/activate-${activationType}-authorization`;
    // returning as the call from confirm and submit is a promise looking for the return statements
    return http
      .post(URL, postBody)
      .then(({ data }) => {
        if (locationId === id || productId === id) {
          dispatch(updateSelfAuthorizations({ data, listType }));
          if (listType === 'customProducts' && data.baseProductSelfAuthorizationsActivated) {
            const baseProductActivations = data.baseProductSelfAuthorizationsActivated;
            R.forEach(
              baseProduct =>
                dispatch(updateSelfAuthorizations({ data: baseProduct, listType: 'baseProducts' })),
              baseProductActivations
            );
          }
        }
        return { error: '', successful: true };
      })
      .catch(err => {
        dispatch(
          createSnack({
            message: `Error activating ${activationType} authorization: ${err.response.data.title}`,
            options: {
              variant: 'error',
            },
          })
        );
        return { error: '', successful: false };
      });
  };

export default reducer;
